<template>
	<v-dialog
		v-model="qrDialog"
		fullscreen
		hide-overlay
		transition="dialog-bottom-transition"
	>
		<div class="qr-container">
			<!-- Header -->
			<div class="qr-header" :class="{ 'header-ios': isIOS }">
				<button class="close-btn" @click="closed">
					<v-icon size="24" color="#333">mdi-close</v-icon>
				</button>
				<h1 class="header-title">{{ $t("global.components.qrReader.txtTitle") }}</h1>
				<div class="header-spacer"></div>
			</div>

			<!-- Selection Mode -->
			<div v-if="select && !qrActive" class="selection-content">
				<div class="selection-header">
					<div class="selection-icon">
						<v-icon size="32" color="#6200ea">mdi-qrcode-scan</v-icon>
					</div>
					<h2 class="selection-title">{{ $t("global.components.qrReader.txtOptions") }}</h2>
					<p class="selection-subtitle">{{ $t("global.components.qrReader.txtInfoSelect") }}</p>
				</div>

				<div class="options-grid">
					<button class="option-card" @click.prevent="dialogCheck = true">
						<div class="option-icon">
							<v-icon size="32" color="#6200ea">mdi-account-check-outline</v-icon>
						</div>
						<span class="option-label">Manual</span>
						<span class="option-desc">{{ $t("qrReader.manualDesc") || 'Check-in manual' }}</span>
					</button>

					<button class="option-card option-primary" @click="qrActive = true">
						<div class="option-icon option-icon-primary">
							<v-icon size="32" color="white">mdi-qrcode-scan</v-icon>
						</div>
						<span class="option-label">QR Code</span>
						<span class="option-desc">{{ $t("qrReader.qrDesc") || 'Escanear código' }}</span>
					</button>
				</div>
			</div>

			<!-- QR Scanner Mode -->
			<div v-if="qrActive || !select" class="scanner-content">
				<div v-if="qrDialog" class="scanner-wrapper">
					<qrcode-stream @decode="onDecode" @init="onInit">
						<div v-if="loading" class="scanner-loading">
							<v-progress-circular
								indeterminate
								color="#6200ea"
								size="48"
								width="4"
							></v-progress-circular>
							<span class="loading-text">{{ $t("qrReader.initializing") || 'Iniciando cámara...' }}</span>
						</div>
					</qrcode-stream>
					<div class="scanner-overlay">
						<div class="scanner-frame"></div>
					</div>
				</div>

				<div class="scanner-info">
					<v-icon size="20" color="#6200ea" class="mr-2">mdi-information-outline</v-icon>
					<span>{{ $t("global.components.qrReader.txtInfo") }}</span>
				</div>

				<div class="scanner-actions">
					<button class="cancel-btn" @click="closed">
						{{ $t("global.dialog.btnCancel") }}
					</button>
				</div>
			</div>
		</div>

		<!-- Manual Check-in Dialog -->
		<v-dialog v-model="dialogCheck" max-width="320" content-class="dialog-modern">
			<div class="confirm-dialog">
				<div class="dialog-header">
					<div class="dialog-icon">
						<v-icon size="28" color="#6200ea">mdi-account-check</v-icon>
					</div>
					<h3 class="dialog-title">{{ $t("global.components.qrReader.txtTitleCheckinM") }}</h3>
					<p class="dialog-desc">{{ $t("global.components.qrReader.txtDescM") }}</p>
				</div>

				<div class="dialog-actions">
					<button class="btn-cancel" @click="dialogCheck = false">
						{{ $t("global.dialog.btnCancel") }}
					</button>
					<button class="btn-confirm" @click.prevent="acepptCheck()">
						<v-icon size="18" color="white" class="mr-1">mdi-check</v-icon>
						Check-in
					</button>
				</div>
			</div>
		</v-dialog>
	</v-dialog>
</template>

<script>
import { QrcodeStream } from "vue-qrcode-reader";
import { mapMutations, mapActions } from "vuex";
import { mapGetters } from "vuex"

export default {
	name: "QRReader",
	components: { QrcodeStream },
	props: {
		onlyEvent: {
			type: Boolean,
			default: function() {
				return false;
			},
		},
		select: {
			type: Boolean,
			default: function() {
				return false;
			},
		},
	},
	data: () => ({
		loading: false,
		qrActive: false,
		dialogCheck: false,
	}),
	computed: {
		...mapGetters(["isIOS"]),
		qrDialog: {
			get: function() {
				return this.$store.state.ui.qr.active;
			},
			set: function() {
				this.closedQRReader();
			},
		},
	},
	methods: {
		...mapMutations("ui", {
			changeQRData: "changeQRData",
			closedQRReader: "closedQRReader",
			openSnackbar: "openSnackbar",
		}),
		...mapActions("ui", { closedQRReader: "closedQRReader" }),
		acepptCheck() {
			this.$bus.emit("onDecode", {
				check: true,
			});
			this.dialogCheck = false;
			this.qrDialog = false;
		},
		onDecode(decodedString) {
			const part = decodedString.split("/");
			const idService = part[part.length - 1];

			this.changeQRData(decodedString);

			this.$axios
				.get(`driver/transport-services/${idService}`)
				.then(() => {
					if (this.onlyEvent) {
						this.$bus.emit("onDecode", {
							id: part[part.length - 1],
						});
						this.qrDialog = false;
					} else {
						setImmediate(() => {
							this.closedQRReader();
							this.$router.push({
								name: "booking",
								params: { id: part[part.length - 1] },
								query: { checkin: true },
							});
						});
					}
				})
				.catch((error) => {
					setImmediate(() => {
						this.closedQRReader();
						if (error.response.status === 404) {
							this.openSnackbar(
								this.$t("global.components.qrReader.txtServiceNoAsign")
							);
						} else {
							this.openSnackbar(
								this.$t("global.components.qrReader.txtErrorGeneric")
							);
						}
					});
				});
		},
		async onInit(promise) {
			this.loading = true;
			try {
				await promise;
			} catch (error) {
				this.$emit("onError", error);
				console.error(error);
			} finally {
				this.loading = false;
			}
		},
		closed() {
			this.qrDialog = false;
			this.qrActive = false;
		},
	},
	mounted() {
		if (!this.select) {
			this.activeQr = true;
		}
	},
};
</script>

<style scoped>
.qr-container {
	min-height: 100vh;
	background: #fafafa;
	display: flex;
	flex-direction: column;
}

.qr-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 12px 16px;
	background: #fff;
	border-bottom: 1px solid #eee;
}

.header-ios {
	padding-top: 48px;
}

.close-btn {
	width: 40px;
	height: 40px;
	border-radius: 10px;
	border: none;
	background: #f5f5f5;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: all 0.15s ease;
}

.close-btn:active {
	transform: scale(0.95);
	background: #eee;
}

.header-title {
	font-size: 17px;
	font-weight: 600;
	color: #1a1a1a;
	margin: 0;
}

.header-spacer {
	width: 40px;
}

/* Selection Mode */
.selection-content {
	flex: 1;
	padding: 24px 16px;
}

.selection-header {
	text-align: center;
	margin-bottom: 32px;
}

.selection-icon {
	width: 72px;
	height: 72px;
	border-radius: 50%;
	background: #ede7f6;
	display: flex;
	align-items: center;
	justify-content: center;
	margin: 0 auto 16px;
}

.selection-title {
	font-size: 22px;
	font-weight: 600;
	color: #1a1a1a;
	margin: 0 0 8px;
}

.selection-subtitle {
	font-size: 14px;
	color: #666;
	margin: 0;
	line-height: 1.5;
}

.options-grid {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 12px;
}

.option-card {
	background: #fff;
	border: 2px solid #e5e5e5;
	border-radius: 16px;
	padding: 24px 16px;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 12px;
	cursor: pointer;
	transition: all 0.15s ease;
}

.option-card:active {
	transform: scale(0.98);
}

.option-primary {
	border-color: #6200ea;
	background: #faf8ff;
}

.option-icon {
	width: 64px;
	height: 64px;
	border-radius: 50%;
	background: #ede7f6;
	display: flex;
	align-items: center;
	justify-content: center;
}

.option-icon-primary {
	background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.option-label {
	font-size: 16px;
	font-weight: 600;
	color: #1a1a1a;
}

.option-desc {
	font-size: 12px;
	color: #888;
}

/* Scanner Mode */
.scanner-content {
	flex: 1;
	display: flex;
	flex-direction: column;
}

.scanner-wrapper {
	position: relative;
	flex: 1;
	background: #000;
	overflow: hidden;
}

.scanner-loading {
	position: absolute;
	inset: 0;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 16px;
	background: rgba(0, 0, 0, 0.8);
	z-index: 10;
}

.loading-text {
	font-size: 14px;
	color: #fff;
}

.scanner-overlay {
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	pointer-events: none;
}

.scanner-frame {
	width: 240px;
	height: 240px;
	border: 3px solid #fff;
	border-radius: 16px;
	box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5);
}

.scanner-info {
	display: flex;
	align-items: center;
	padding: 16px;
	background: #fff;
	font-size: 13px;
	color: #666;
	line-height: 1.4;
}

.scanner-actions {
	padding: 12px 16px 24px;
	background: #fff;
}

.cancel-btn {
	width: 100%;
	padding: 14px;
	border: none;
	border-radius: 10px;
	background: #f5f5f5;
	color: #666;
	font-size: 15px;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.15s ease;
}

.cancel-btn:active {
	transform: scale(0.98);
	background: #eee;
}

/* Confirm Dialog */
.confirm-dialog {
	background: #fff;
	border-radius: 16px;
	overflow: hidden;
}

.dialog-header {
	padding: 24px 20px 16px;
	text-align: center;
}

.dialog-icon {
	width: 56px;
	height: 56px;
	border-radius: 50%;
	background: #ede7f6;
	display: flex;
	align-items: center;
	justify-content: center;
	margin: 0 auto 14px;
}

.dialog-title {
	font-size: 18px;
	font-weight: 600;
	color: #1a1a1a;
	margin: 0 0 8px;
}

.dialog-desc {
	font-size: 13px;
	color: #666;
	margin: 0;
	line-height: 1.5;
}

.dialog-actions {
	display: flex;
	gap: 10px;
	padding: 12px 20px 20px;
}

.btn-cancel,
.btn-confirm {
	flex: 1;
	padding: 12px;
	border-radius: 10px;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.15s ease;
	border: none;
	display: flex;
	align-items: center;
	justify-content: center;
}

.btn-cancel {
	background: #f5f5f5;
	color: #666;
}

.btn-cancel:active {
	background: #eee;
	transform: scale(0.98);
}

.btn-confirm {
	background: #4caf50;
	color: #fff;
}

.btn-confirm:active {
	background: #43a047;
	transform: scale(0.98);
}
</style>

<style>
.dialog-modern {
	border-radius: 16px !important;
	overflow: hidden;
}
</style>
